"use client"

import { injected } from "@wagmi/core"
import type { Chain } from "viem"
import { http, createConfig } from "wagmi"
import {
  arbitrum,
  aurora,
  avalanche,
  base,
  bsc,
  mainnet,
  optimism,
  polygon,
} from "wagmi/chains"
import { coinbaseWallet, walletConnect } from "wagmi/connectors"

import { PROJECT_ID } from "@src/utils/environment"

export const turbo = {
  id: 1313161567,
  name: "TurboChain",
  nativeCurrency: { name: "Turbo", symbol: "TURBO", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc-0x4e45415f.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: { name: "Explorer", url: "https://explorer.turbo.aurora.dev" },
  },
} as const satisfies Chain

export const tuxappchain = {
  id: 1313161573,
  name: "TuxaChain",
  nativeCurrency: { name: "Tuxa", symbol: "TUXA", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc-0x4e454165.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: { name: "Explorer", url: "https://explorer.tuxa.aurora.dev" },
  },
} as const satisfies Chain

export const vertex = {
  id: 1313161587,
  name: "Vertex",
  nativeCurrency: { name: "Aurora", symbol: "AURORA", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc-0x4e454173.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://explorer.0x4e454173.aurora-cloud.dev",
    },
  },
} as const satisfies Chain

export const optima = {
  id: 1313161569,
  name: "Optima",
  nativeCurrency: { name: "Optima", symbol: "OPTIMA", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc-0x4e454161.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://explorer.optima.aurora.dev",
    },
  },
} as const satisfies Chain

export const coineasy = {
  id: 1313161752,
  name: "EasyChain",
  nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://0x4e454218.rpc.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://0x4e454218.explorer.aurora-cloud.dev",
    },
  },
} as const satisfies Chain

export const hako = {
  id: 1313161901,
  name: "Hako",
  nativeCurrency: { name: "Ethereum", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://0x4e4542ad.rpc.aurora-cloud.dev"] },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://0x4e4542ad.explorer.aurora-cloud.dev",
    },
  },
} as const satisfies Chain

export const adi = {
  id: 36900,
  name: "ADI_Chain",
  nativeCurrency: { name: "ADI", symbol: "ADI", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc.adifoundation.ai"] },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://explorer.adifoundation.ai",
    },
  },
} as const satisfies Chain

export const config = createConfig({
  chains: [
    mainnet,
    base,
    arbitrum,
    turbo,
    aurora,
    polygon,
    bsc,
    optimism,
    avalanche,
    tuxappchain,
    vertex,
    optima,
    coineasy,
    hako,
    adi,
  ],
  connectors: [
    PROJECT_ID != null &&
      walletConnect({
        projectId: PROJECT_ID,
        showQrModal: true,
        customStoragePrefix: "near-intents",
      }),
    coinbaseWallet({
      appName: "Near Intents",
      preference: {
        options: "eoaOnly", // smart-wallets can't sign intents
        telemetry: false,
      },
    }),
    injected(),
  ].filter((a): a is Exclude<typeof a, boolean> => !!a),
  transports: {
    [mainnet.id]: http(),
    [base.id]: http(),
    [arbitrum.id]: http(),
    [turbo.id]: http(),
    [aurora.id]: http(),
    [polygon.id]: http(),
    [bsc.id]: http(),
    [optimism.id]: http(),
    [avalanche.id]: http(),
    [tuxappchain.id]: http(),
    [vertex.id]: http(),
    [optima.id]: http(),
    [coineasy.id]: http(),
    [hako.id]: http(),
    [adi.id]: http(),
  },
})
